// Import LitElement base class and html helper function
import {LitElement, html, css, svg} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import {styleMap} from 'lit/directives/style-map.js';

export class StreamUserRowing extends LitElement {
    /**
     * Define properties. Properties defined here will be automatically
     * observed.
     */
    static get properties() {
        return {
            user_name: {type: String},
            max_hr: {type: Number},
            hr: {type: Number},
            percentage_hr: {type: Number, attribute: false},
            power: {type: Number},
            percentage_power: {type: Number, attribute: false},
            cadence: {type: Number},
            ftp: {type: Number},
            box_color: {type: Object, attribute: false},
            hr_zones_range: {type: Array},
            power_zones_range: {type: Array},
            zone_index: {type: Number, attribute: false},
            pace: {type: Number},
            pace_label: {type: String, attribute: false},
            user_photo_url: {type: String}
        };
    }

    static get styles() {
        return css`
            .box-0 {
                background: var(--box-0-default);
            }
            .box-1 {
                background: var(--box-1-default);
            }
            .box-2 {
                background: var(--box-2-default);
            }
            .box-3 {
                background: var(--box-3-default);
            }
            .box-4 {
                background: var(--box-4-default);
            }
            .color-0 {
                color: var(--box-0-default);
            }
            .color-1 {
                color: var(--box-1-default);
            }
            .color-2 {
                color: var(--box-2-default);
            }
            .color-3 {
                color: var(--box-3-default);
            }
            .color-4 {
                color: var(--box-4-default);
            }

            .stream-user {
                width: var(--stream-box-width);
                height: var(--stream-box-height);
                display: flex;
                display: -webkit-flex;
                flex-direction: row;
                flex-wrap: nowrap;
                justify-content: space-between;
                padding: 0;
                background-color: var(--stream-user-background);
                border-radius: var(--stream-user-border-radius);
            }
            .stream-user-left {
                padding: 0.8em 0 0.3em 0.8em;
                flex: 0 1 var(--stream-user-left-width);
            }
            .stream-user-main {
                padding: 1em 0 0.8em 0.4em;
                flex: 2 1 auto;
            }
            .stream-user-right {
                padding: 0.4em 0.8em 0.6em 0.3em;
                flex: 0 1 var(--stream-user-right-width);
                line-height: 140%;
                font-size: 1.5em;
                font-weight: bold;
                text-align: right;
            }
            .stream-user-right .stream-m1 {
                display: inline-block;
                font-size: 0.8em;
                width: 45%;
            }
            .stream-user-right .stream-m1 .stream-text-cadence {
                color: #34c759;
            }
            .stream-user-right .stream-m2 {
                display: inline-block;
                width: 50%;
            }
            .stream-userphoto-container {
                width: var(--stream-user-width);
                height: var(--stream-user-width);
                margin: auto;
            }
            .stream-userphoto-circle {
                max-width: 100%;
                width: 100%;
                height: auto;
                display: block;
                padding-top: 100%;
                border-radius: 50%;
                background-position-x: center;
                background-position-y: center;
                background-size: cover;
                border: 1px solid #eeeeee;
            }
            .icon {
                display: inline-block;
                stroke-width: 0;
                stroke: var(--stream-icon-color);
                fill: var(--stream-icon-color);
                width: 0.6em;
            }
            .icon.w-normal {
                width: 0.7em;
            }
        `;
    }

    constructor() {
        super();
        this.max_hr = 0;
        this.box_color = {'color-0': true};
        this.zones_range = [0,60,70,80,90];
        this.user_name = 'Loading';
        this.percentage_hr = 0;
        this.hr = 0;
        this.cadence = 0;
        this.power = 0;
        this.ftp = 0;
        this.percentage_power = 0;
        this.power_zones_range = [0,55,75,90,105,120,150];
        this.zone_index = null;
        this.pace = 0;
        this.pace_label = '0:00';
        this.user_photo_url = '/img/user_sagoma.jpg';
    }

    willUpdate(changedProperties) {
        if (changedProperties.has('hr')) {
            if (this.max_hr > 0) {
                this.percentage_hr = Math.round((this.hr * 100) / this.max_hr);
            }
            let z_index = 0;
            for(var i=1; i < this.zones_range.length; i++){
                if (this.percentage_hr <= this.zones_range[i]){
                    break;
                }
                z_index = z_index + 1;
            }
            let zone_key = 'color-' + z_index;
            this.box_color = {[zone_key]: true};
            this.zone_index = z_index + 1;
        }
        if (changedProperties.has('power')) {
            if (this.power !== null && this.power !== 'undefined' && this.power !== 0 && this.power !== '') {
                if (this.ftp > 0) {
                    this.percentage_power = Math.round((this.power * 100) / this.ftp);
                }
            }
        }
        if (changedProperties.has('pace')) {
            if (this.cadence !== null && this.cadence !== 'undefined' && this.cadence > 0 ) {
                this.pace_label = this.distance_of_time(this.cadence);
            } else {
                this.pace_label = '0:00';
            }
        }
    }

    render() {
        const userPhotoStyles = { backgroundImage: 'url(' + this.user_photo_url + ')' };
        return html`
            <div class="stream-user">
                <div class="stream-user-left">
                    <div class="stream-userphoto-container">
                        <div class="stream-userphoto-circle" style=${styleMap(userPhotoStyles)}></div>
                    </div>
                </div>
                <div class="stream-user-main">
                    ${this.user_name}
                </div>
                <div class="stream-user-right">
                    <div class="stream-m1"><span class="stream-text-cadence">${this.cadence}</span> <span>${this.svgCadenceIcon}</span></div>
                    <div class="stream-m2"><span class="${classMap(this.box_color)}">${this.hr}</span> <span>${this.svgHrIcon}</span></div>
                </div>
            </div>
        `;
    }

    get svgHrIcon() {
        return svg`<svg viewBox="0 0 100 100" class="icon"><use xlink:href="/img/stream-icons.svg#icon-heart" href="/img/stream-icons.svg#icon-heart"/></svg>`;
    }

    get svgCadenceIcon() {
        return svg`<svg viewBox="0 0 187 155" class="icon w-normal"><use xlink:href="/img/stream-icons.svg#icon-rowing" href="/img/stream-icons.svg#icon-rowing"/></svg>`;
    }

    distance_of_time(val) {
        let output = '';
        let time_in_seconds = val;
        if (time_in_seconds >= 60) {
            let minutes = Math.floor(val / 60)
            time_in_seconds = Math.round((time_in_seconds - (60 * minutes)));
            output += minutes + ':'
        } else {
            output += '0:'
        }
        if (time_in_seconds < 10) {
            output += '0' + time_in_seconds;
        } else {
            output += '' + time_in_seconds;
        }
        return output;
    }

}
